/**
 * Reader UI - 2D RSVP Reader Interface
 *
 * Main reader page with:
 * - Glass-panel RSVP word display (one word at a time)
 * - Transport controls: Play/Pause, Rewind −10, Fast-Forward +10, Restart
 * - WPM slider (enabled during playback)
 * - Progress indicator
 *
 * All styling uses tokens.css variables. Cyan accent is focus/active only.
 */

import { ReaderCore, createReaderCore, type ReaderStatus } from '../../core/readerCore';
import { PACING_CONFIG } from '../../core/pacing';
import { getSampleText } from '../../content/sampleText';
import { extractPdfText } from '../../ingest/pdfExtract';
import { cleanPdfText, type CleanupResult } from '../../ingest/pdfClean';
import { initPairingUI, cleanupPairingUI } from './pairingUI';
import { getXRManager, isXRAvailable } from './xr';
import { generateSessionCode, buildPhoneUrl } from '../../pairing';
import QRCode from 'qrcode';

/**
 * Create SVG icon element for transport controls (flat style)
 */
function createIcon(name: 'play' | 'pause' | 'skipStart' | 'skipEnd' | 'rewind' | 'fastForward' | 'restart', size = 20): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const paths: Record<string, string> = {
    play: 'M8 5v14l11-7z',
    pause: 'M6 4h4v16H6zm8 0h4v16h-4z',
    skipStart: 'M6 6h2v12H6zm3.5 6 8.5 6V6z',
    skipEnd: 'M6 18l8.5-6L6 6v12zm10-12h2v12h-2z',
    rewind: 'M11 18V6l-8.5 6 8.5 6zm.5-6 8.5 6V6l-8.5 6z',
    fastForward: 'M4 18l8.5-6L4 6v12zm9-12v12l8.5-6-8.5-6z',
    restart: 'M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z',
  };
  path.setAttribute('d', paths[name]);
  svg.appendChild(path);
  return svg;
}

/**
 * Replace button icon content
 */
function setButtonIcon(btn: HTMLButtonElement, icon: SVGSVGElement): void {
  btn.textContent = '';
  btn.appendChild(icon);
}

/**
 * Reader UI state
 */
interface ReaderUIState {
  core: ReaderCore;
  animationFrameId: number | null;
  lastTickTime: number;
  cleanupPairing: (() => void) | null;
  cleanupXR: (() => Promise<void>) | null;
  lastPdfResult: CleanupResult | null;
}

/**
 * DOM element references
 */
interface ReaderElements {
  container: HTMLElement;
  // Landing elements
  landingContent: HTMLElement;
  pageTitle: HTMLElement;
  textInput: HTMLTextAreaElement;
  sampleTextBtn: HTMLButtonElement;
  pdfImportBtn: HTMLButtonElement;
  pdfFileInput: HTMLInputElement;
  pdfStatus: HTMLElement;
  loadTextBtn: HTMLButtonElement;
  landingEnterXRBtn: HTMLButtonElement;
  landingQRCanvas: HTMLCanvasElement;
  landingSessionCode: HTMLElement;
  // Reader elements
  readerContent: HTMLElement;
  docTitle: HTMLElement;
  rsvpPanel: HTMLElement;
  wordDisplay: HTMLElement;
  finishedIcon: HTMLElement;
  finishedText: HTMLElement;
  progressText: HTMLElement;
  progressSlider: HTMLInputElement;
  skipStartBtn: HTMLButtonElement;
  rewindBtn: HTMLButtonElement;
  playPauseBtn: HTMLButtonElement;
  fastForwardBtn: HTMLButtonElement;
  skipEndBtn: HTMLButtonElement;
  wpmValue: HTMLElement;
  wpmSlider: HTMLInputElement;
  readerSampleTextBtn: HTMLButtonElement;
  readerLoadTextBtn: HTMLButtonElement;
  enterXRBtn: HTMLButtonElement;
  keyboardHints: HTMLElement;
  // Mobile controls
  mobileControls: HTMLElement;
  mobileSkipStartBtn: HTMLButtonElement;
  mobileRewindBtn: HTMLButtonElement;
  mobilePlayPauseBtn: HTMLButtonElement;
  mobileFastForwardBtn: HTMLButtonElement;
  mobileSkipEndBtn: HTMLButtonElement;
  mobileCameraToggle: HTMLButtonElement;
  mobileExitXRBtn: HTMLButtonElement;
  mobileWpmValue: HTMLElement;
  mobileWpmSlider: HTMLInputElement;
  // Finished modal
  finishedModal: HTMLElement;
  modalCloseBtn: HTMLButtonElement;
  modalRestartBtn: HTMLButtonElement;
  modalReadAgainBtn: HTMLButtonElement;
}

// Module state
let state: ReaderUIState | null = null;
let elements: ReaderElements | null = null;

/**
 * Initialize the Reader UI
 */
export function initReaderUI(container: HTMLElement): void {
  // Clear container
  container.textContent = '';

  // Create core
  const core = createReaderCore();

  // Create UI elements
  const els = createReaderElements(container);

  // Store state
  state = {
    core,
    animationFrameId: null,
    lastTickTime: 0,
    cleanupPairing: null,
    cleanupXR: null,
    lastPdfResult: null,
  };
  elements = els;

  // Store XR cleanup function
  state.cleanupXR = async () => {
    const manager = getXRManager();
    if (manager.active) {
      await manager.endSession();
    }
  };

  // Generate a single session code for both pairing panel and landing QR
  const sessionCode = generateSessionCode();
  const phoneUrl = buildPhoneUrl(window.location.origin, sessionCode);

  // Initialize pairing UI (adds QR code panel for reader mode) with shared session code
  state.cleanupPairing = initPairingUI(container, core, sessionCode);

  // Initialize landing QR code (inline in landing card) with same session code
  els.landingSessionCode.textContent = sessionCode;
  generateLandingQRCode(els.landingQRCanvas, phoneUrl);

  // Initialize Enter XR button state (reader screen)
  isXRAvailable().then((available) => {
    els.enterXRBtn.disabled = false;
    // Ensure button shows "Enter XR Mode" on init
    els.enterXRBtn.textContent = '';
    els.enterXRBtn.appendChild(document.createTextNode('Enter'));
    els.enterXRBtn.appendChild(document.createElement('br'));
    els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
    if (!available) {
      els.enterXRBtn.title = 'WebXR not available - will use 3D preview mode';
    }
  });

  // Initialize landing Enter XR button state
  // Enable button even without WebXR - will use preview mode as fallback
  isXRAvailable().then((available) => {
    // Always enable - use preview mode if WebXR unavailable
    els.landingEnterXRBtn.disabled = false;
    if (!available) {
      els.landingEnterXRBtn.title = 'WebXR not available - will use 3D preview mode';
    }
  });

  // Subscribe to core events
  core.on((event) => {
    switch (event.type) {
      case 'stateChange':
        updateUIForStatus(event.status);
        break;
      case 'wordChange':
        updateWordDisplay(event.word);
        break;
      case 'progress':
        updateProgress(event.index, event.total);
        break;
      case 'finished':
        handleFinished();
        break;
    }
  });

  // Wire up event handlers
  wireEventHandlers(els, core);

  // Set initial UI state
  updateUIForStatus(core.status);
  updateWpmDisplay(core.wpm);
}

/**
 * Create all DOM elements for the reader UI
 * Matches reference images in docs/ui/references/
 */
function createReaderElements(container: HTMLElement): ReaderElements {
  container.className = 'reader-container';

  // ==========================================================================
  // Header with logo
  // ==========================================================================
  const header = document.createElement('header');
  header.className = 'reader-header';

  // Logo link - clicking returns to landing page
  const logoLink = document.createElement('a');
  logoLink.href = '/';
  logoLink.className = 'logo-link';
  logoLink.setAttribute('aria-label', 'Return to landing page');

  const title = document.createElement('h1');
  title.className = 'reader-title';
  // Split logo: "RSVP" white + "XR" cyan
  const brandRsvp = document.createElement('span');
  brandRsvp.className = 'brand-rsvp';
  brandRsvp.textContent = 'RSVP';
  const brandXr = document.createElement('span');
  brandXr.className = 'brand-xr';
  brandXr.textContent = 'XR';
  title.appendChild(brandRsvp);
  title.appendChild(brandXr);
  logoLink.appendChild(title);
  header.appendChild(logoLink);

  container.appendChild(header);

  // ==========================================================================
  // Landing Content (shown when idle)
  // ==========================================================================
  const landingContent = document.createElement('div');
  landingContent.className = 'reader-main landing-content';

  const pageTitle = document.createElement('h2');
  pageTitle.className = 'page-title';
  pageTitle.textContent = 'Paste text and load to read:';
  landingContent.appendChild(pageTitle);

  // Glass card containing textarea + QR section
  const landingCard = document.createElement('div');
  landingCard.className = 'landing-card';

  // Left: textarea section
  const inputSection = document.createElement('div');
  inputSection.className = 'landing-input-section';

  const textInput = document.createElement('textarea');
  textInput.className = 'landing-textarea';
  textInput.placeholder = 'Paste your text here...';
  inputSection.appendChild(textInput);

  landingCard.appendChild(inputSection);

  // Right: QR section
  const qrSection = document.createElement('div');
  qrSection.className = 'landing-qr-section';

  const qrTitle = document.createElement('div');
  qrTitle.className = 'landing-qr-title';
  qrTitle.textContent = 'Connect Phone';
  qrSection.appendChild(qrTitle);

  const qrCanvasWrap = document.createElement('div');
  qrCanvasWrap.className = 'landing-qr-canvas-wrap';

  const landingQRCanvas = document.createElement('canvas');
  landingQRCanvas.className = 'landing-qr-canvas';
  landingQRCanvas.width = 120;
  landingQRCanvas.height = 120;
  qrCanvasWrap.appendChild(landingQRCanvas);
  qrSection.appendChild(qrCanvasWrap);

  const landingSessionCode = document.createElement('div');
  landingSessionCode.className = 'landing-session-code';
  const sessionLabel = document.createTextNode('Session code: ');
  const sessionCodeStrong = document.createElement('strong');
  landingSessionCode.appendChild(sessionLabel);
  landingSessionCode.appendChild(sessionCodeStrong);
  qrSection.appendChild(landingSessionCode);

  const qrHelper = document.createElement('div');
  qrHelper.className = 'landing-qr-helper';
  qrHelper.textContent = 'Scan QR code for XR controls';
  qrSection.appendChild(qrHelper);

  landingCard.appendChild(qrSection);
  landingContent.appendChild(landingCard);

  // Action buttons row
  const landingActions = document.createElement('div');
  landingActions.className = 'action-buttons';

  const sampleTextBtn = createPillButton('sample-text-btn', '📄', 'Load Sample', 'Load sample text');
  landingActions.appendChild(sampleTextBtn);

  const pdfImportBtn = createPillButton('pdf-import-btn', '📑', 'Import PDF', 'Import text from PDF');
  landingActions.appendChild(pdfImportBtn);

  // Hidden file input for PDF selection
  const pdfFileInput = document.createElement('input');
  pdfFileInput.type = 'file';
  pdfFileInput.accept = '.pdf,application/pdf';
  pdfFileInput.className = 'pdf-file-input';
  pdfFileInput.hidden = true;
  landingActions.appendChild(pdfFileInput);

  const loadTextBtn = createPillButton('load-text-btn', '↓', 'Load Text', 'Load text for reading');
  loadTextBtn.classList.add('primary');
  landingActions.appendChild(loadTextBtn);

  landingContent.appendChild(landingActions);

  // PDF status message (for errors/warnings)
  const pdfStatus = document.createElement('div');
  pdfStatus.className = 'pdf-status';
  pdfStatus.hidden = true;
  landingContent.appendChild(pdfStatus);

  // Enter XR button on landing
  const landingEnterXRBtn = document.createElement('button');
  landingEnterXRBtn.type = 'button';
  landingEnterXRBtn.className = 'landing-enter-xr';
  const landingXRIcon = document.createElement('span');
  landingXRIcon.className = 'icon';
  landingXRIcon.textContent = '◉';
  landingEnterXRBtn.appendChild(landingXRIcon);
  landingEnterXRBtn.appendChild(document.createTextNode(' Enter XR'));
  landingEnterXRBtn.disabled = true;
  landingContent.appendChild(landingEnterXRBtn);

  container.appendChild(landingContent);

  // ==========================================================================
  // Reader Content (shown when text loaded)
  // ==========================================================================
  const readerContent = document.createElement('div');
  readerContent.className = 'reader-main';
  readerContent.hidden = true;

  // Document title (gray, truncated) - hidden by default, shown when title available
  const docTitle = document.createElement('div');
  docTitle.className = 'doc-title';
  docTitle.textContent = '';
  docTitle.hidden = true;
  readerContent.appendChild(docTitle);

  // RSVP Panel (glass card)
  const rsvpPanel = document.createElement('div');
  rsvpPanel.className = 'rsvp-panel';

  const wordDisplay = document.createElement('div');
  wordDisplay.className = 'rsvp-word';
  wordDisplay.setAttribute('aria-live', 'polite');
  wordDisplay.setAttribute('aria-atomic', 'true');
  rsvpPanel.appendChild(wordDisplay);

  // Finished state elements (hidden by default)
  const finishedIcon = document.createElement('div');
  finishedIcon.className = 'finished-icon';
  finishedIcon.textContent = '✓';
  finishedIcon.hidden = true;
  rsvpPanel.appendChild(finishedIcon);

  const finishedText = document.createElement('div');
  finishedText.className = 'finished-text';
  finishedText.textContent = 'Finished';
  finishedText.hidden = true;
  rsvpPanel.appendChild(finishedText);

  readerContent.appendChild(rsvpPanel);

  // Progress area
  const progressArea = document.createElement('div');
  progressArea.className = 'progress-area';

  const progressText = document.createElement('div');
  progressText.className = 'progress-text';
  progressText.textContent = '0/0 words';
  progressArea.appendChild(progressText);

  const progressSlider = document.createElement('input');
  progressSlider.type = 'range';
  progressSlider.className = 'progress-slider';
  progressSlider.min = '0';
  progressSlider.max = '100';
  progressSlider.value = '0';
  progressSlider.setAttribute('aria-label', 'Reading progress');
  progressArea.appendChild(progressSlider);

  readerContent.appendChild(progressArea);

  // Transport controls (5 buttons: skip-start, rewind, play/pause, fast-forward, skip-end)
  const transport = document.createElement('div');
  transport.className = 'transport-controls';

  const skipStartBtn = createTransportButton('skip-start-btn', createIcon('skipStart'), 'Skip to start');
  const rewindBtn = createTransportButton('rewind-btn', createIcon('rewind'), 'Rewind 10 words');
  const playPauseBtn = createTransportButton('play-pause-btn', createIcon('play', 24), 'Play');
  playPauseBtn.classList.add('primary');
  const fastForwardBtn = createTransportButton('ff-btn', createIcon('fastForward'), 'Forward 10 words');
  const skipEndBtn = createTransportButton('skip-end-btn', createIcon('skipEnd'), 'Skip to end');

  transport.appendChild(skipStartBtn);
  transport.appendChild(rewindBtn);
  transport.appendChild(playPauseBtn);
  transport.appendChild(fastForwardBtn);
  transport.appendChild(skipEndBtn);

  readerContent.appendChild(transport);

  // WPM control (value above slider)
  const wpmControl = document.createElement('div');
  wpmControl.className = 'wpm-control';

  const wpmDisplay = document.createElement('div');
  wpmDisplay.className = 'wpm-display';
  const wpmValue = document.createElement('span');
  wpmValue.className = 'wpm-value';
  wpmValue.textContent = String(PACING_CONFIG.WPM_DEFAULT);
  const wpmLabel = document.createElement('span');
  wpmLabel.className = 'wpm-label';
  wpmLabel.textContent = 'WPM';
  wpmDisplay.appendChild(wpmValue);
  wpmDisplay.appendChild(wpmLabel);
  wpmControl.appendChild(wpmDisplay);

  const wpmSlider = document.createElement('input');
  wpmSlider.type = 'range';
  wpmSlider.id = 'wpm-slider';
  wpmSlider.className = 'wpm-slider';
  wpmSlider.min = String(PACING_CONFIG.WPM_MIN);
  wpmSlider.max = String(PACING_CONFIG.WPM_MAX);
  wpmSlider.value = String(PACING_CONFIG.WPM_DEFAULT);
  wpmSlider.step = '25';
  wpmControl.appendChild(wpmSlider);

  readerContent.appendChild(wpmControl);

  // Reader action buttons (three across, no icons to save space)
  const readerActions = document.createElement('div');
  readerActions.className = 'action-buttons compact';

  const readerSampleTextBtn = document.createElement('button');
  readerSampleTextBtn.type = 'button';
  readerSampleTextBtn.id = 'reader-sample-btn';
  readerSampleTextBtn.className = 'pill-btn two-line';
  readerSampleTextBtn.appendChild(document.createTextNode('Load'));
  readerSampleTextBtn.appendChild(document.createElement('br'));
  readerSampleTextBtn.appendChild(document.createTextNode('Sample'));
  readerSampleTextBtn.title = 'Load sample text';
  readerActions.appendChild(readerSampleTextBtn);

  const readerLoadTextBtn = document.createElement('button');
  readerLoadTextBtn.type = 'button';
  readerLoadTextBtn.id = 'reader-load-btn';
  readerLoadTextBtn.className = 'pill-btn two-line';
  readerLoadTextBtn.appendChild(document.createTextNode('Load'));
  readerLoadTextBtn.appendChild(document.createElement('br'));
  readerLoadTextBtn.appendChild(document.createTextNode('Text'));
  readerLoadTextBtn.title = 'Load new text';
  readerActions.appendChild(readerLoadTextBtn);

  // Enter XR Mode button (in same row)
  const enterXRBtn = document.createElement('button');
  enterXRBtn.type = 'button';
  enterXRBtn.className = 'pill-btn enter-xr-btn two-line';
  enterXRBtn.appendChild(document.createTextNode('Enter'));
  enterXRBtn.appendChild(document.createElement('br'));
  enterXRBtn.appendChild(document.createTextNode('XR Mode'));
  enterXRBtn.title = 'Enter immersive reading mode';
  enterXRBtn.disabled = true;
  readerActions.appendChild(enterXRBtn);

  readerContent.appendChild(readerActions);

  container.appendChild(readerContent);

  // ==========================================================================
  // Keyboard hints footer
  // ==========================================================================
  const keyboardHints = document.createElement('div');
  keyboardHints.className = 'keyboard-hints';

  const hints = [
    { key: 'Space', action: 'Play/Pause' },
    { key: '↑↓', action: 'WPM' },
    { key: '←', action: 'Rewind' },
    { key: 'R', action: 'Restart' },
  ];

  hints.forEach(hint => {
    const span = document.createElement('span');
    span.className = 'keyboard-hint';
    const kbd = document.createElement('kbd');
    kbd.textContent = hint.key;
    span.appendChild(kbd);
    span.appendChild(document.createTextNode(': ' + hint.action));
    keyboardHints.appendChild(span);
  });

  container.appendChild(keyboardHints);

  // ==========================================================================
  // Mobile Fixed Controls (shown only on mobile, fixed at bottom)
  // ==========================================================================
  const mobileControls = document.createElement('div');
  mobileControls.className = 'mobile-controls';

  // Mobile transport
  const mobileTransport = document.createElement('div');
  mobileTransport.className = 'mobile-transport';

  const mobileSkipStartBtn = createTransportButton('mobile-skip-start', createIcon('skipStart'), 'Skip to start');
  const mobileRewindBtn = createTransportButton('mobile-rewind', createIcon('rewind'), 'Rewind 10 words');
  const mobilePlayPauseBtn = createTransportButton('mobile-play-pause', createIcon('play', 24), 'Play');
  mobilePlayPauseBtn.classList.add('primary');
  const mobileFastForwardBtn = createTransportButton('mobile-ff', createIcon('fastForward'), 'Forward 10 words');
  const mobileSkipEndBtn = createTransportButton('mobile-skip-end', createIcon('skipEnd'), 'Skip to end');

  mobileTransport.appendChild(mobileSkipStartBtn);
  mobileTransport.appendChild(mobileRewindBtn);
  mobileTransport.appendChild(mobilePlayPauseBtn);
  mobileTransport.appendChild(mobileFastForwardBtn);
  mobileTransport.appendChild(mobileSkipEndBtn);

  mobileControls.appendChild(mobileTransport);

  // Mobile XR buttons row (camera toggle + exit button)
  const mobileXRButtons = document.createElement('div');
  mobileXRButtons.className = 'mobile-xr-buttons';
  mobileXRButtons.hidden = true; // Hidden until XR preview starts

  // Camera toggle (AR mode)
  const mobileCameraToggle = document.createElement('button');
  mobileCameraToggle.type = 'button';
  mobileCameraToggle.className = 'mobile-camera-toggle';
  mobileCameraToggle.textContent = 'Start Camera';
  mobileXRButtons.appendChild(mobileCameraToggle);

  // Exit XR button (always visible when XR preview is active)
  const mobileExitXRBtn = document.createElement('button');
  mobileExitXRBtn.type = 'button';
  mobileExitXRBtn.className = 'mobile-camera-toggle';
  mobileExitXRBtn.textContent = 'Exit XR';
  mobileXRButtons.appendChild(mobileExitXRBtn);

  mobileControls.appendChild(mobileXRButtons);

  // Mobile WPM
  const mobileWpmControl = document.createElement('div');
  mobileWpmControl.className = 'mobile-wpm-control';

  const mobileWpmDisplay = document.createElement('div');
  mobileWpmDisplay.className = 'wpm-display';
  const mobileWpmValue = document.createElement('span');
  mobileWpmValue.className = 'wpm-value mobile-wpm-value';
  mobileWpmValue.textContent = String(PACING_CONFIG.WPM_DEFAULT);
  const mobileWpmLabel = document.createElement('span');
  mobileWpmLabel.className = 'wpm-label';
  mobileWpmLabel.textContent = 'WPM';
  mobileWpmDisplay.appendChild(mobileWpmValue);
  mobileWpmDisplay.appendChild(mobileWpmLabel);
  mobileWpmControl.appendChild(mobileWpmDisplay);

  const mobileWpmSlider = document.createElement('input');
  mobileWpmSlider.type = 'range';
  mobileWpmSlider.className = 'wpm-slider mobile-wpm-slider';
  mobileWpmSlider.min = String(PACING_CONFIG.WPM_MIN);
  mobileWpmSlider.max = String(PACING_CONFIG.WPM_MAX);
  mobileWpmSlider.value = String(PACING_CONFIG.WPM_DEFAULT);
  mobileWpmSlider.step = '25';
  mobileWpmControl.appendChild(mobileWpmSlider);

  mobileControls.appendChild(mobileWpmControl);

  container.appendChild(mobileControls);

  // ==========================================================================
  // Finished Modal (FR-009 through FR-012)
  // ==========================================================================
  const finishedModal = document.createElement('div');
  finishedModal.className = 'finished-modal';
  finishedModal.hidden = true;

  const modalBackdrop = document.createElement('div');
  modalBackdrop.className = 'modal-backdrop';
  finishedModal.appendChild(modalBackdrop);

  const modalContent = document.createElement('div');
  modalContent.className = 'modal-content';

  // Modal header with title and X button
  const modalHeader = document.createElement('div');
  modalHeader.className = 'modal-header';

  const modalTitle = document.createElement('h2');
  modalTitle.className = 'modal-title';
  modalTitle.textContent = 'Finished';
  modalHeader.appendChild(modalTitle);

  const modalCloseBtn = document.createElement('button');
  modalCloseBtn.type = 'button';
  modalCloseBtn.className = 'modal-close-btn';
  modalCloseBtn.textContent = '×';
  modalCloseBtn.title = 'Close';
  modalCloseBtn.setAttribute('aria-label', 'Close');
  modalHeader.appendChild(modalCloseBtn);

  modalContent.appendChild(modalHeader);

  // Modal body
  const modalBody = document.createElement('div');
  modalBody.className = 'modal-body';

  const modalMessage = document.createElement('p');
  modalMessage.className = 'modal-message';
  modalMessage.textContent = 'You\'ve finished reading!';
  modalBody.appendChild(modalMessage);

  modalContent.appendChild(modalBody);

  // Modal actions
  const modalActions = document.createElement('div');
  modalActions.className = 'modal-actions';

  const modalRestartBtn = document.createElement('button');
  modalRestartBtn.type = 'button';
  modalRestartBtn.className = 'modal-btn';
  modalRestartBtn.textContent = 'Restart';
  modalRestartBtn.title = 'Return to landing page';
  modalActions.appendChild(modalRestartBtn);

  const modalReadAgainBtn = document.createElement('button');
  modalReadAgainBtn.type = 'button';
  modalReadAgainBtn.className = 'modal-btn primary';
  modalReadAgainBtn.textContent = 'Read again';
  modalReadAgainBtn.title = 'Reset to first word';
  modalActions.appendChild(modalReadAgainBtn);

  modalContent.appendChild(modalActions);
  finishedModal.appendChild(modalContent);
  container.appendChild(finishedModal);

  return {
    container,
    // Landing
    landingContent,
    pageTitle,
    textInput,
    sampleTextBtn,
    pdfImportBtn,
    pdfFileInput,
    pdfStatus,
    loadTextBtn,
    landingEnterXRBtn,
    landingQRCanvas,
    landingSessionCode: sessionCodeStrong,
    // Reader
    readerContent,
    docTitle,
    rsvpPanel,
    wordDisplay,
    finishedIcon,
    finishedText,
    progressText,
    progressSlider,
    skipStartBtn,
    rewindBtn,
    playPauseBtn,
    fastForwardBtn,
    skipEndBtn,
    wpmValue,
    wpmSlider,
    readerSampleTextBtn,
    readerLoadTextBtn,
    enterXRBtn,
    keyboardHints,
    // Mobile controls
    mobileControls,
    mobileSkipStartBtn,
    mobileRewindBtn,
    mobilePlayPauseBtn,
    mobileFastForwardBtn,
    mobileSkipEndBtn,
    mobileCameraToggle,
    mobileExitXRBtn,
    mobileWpmValue,
    mobileWpmSlider,
    // Finished modal
    finishedModal,
    modalCloseBtn,
    modalRestartBtn,
    modalReadAgainBtn,
  };
}

/**
 * Create a pill-shaped action button with icon
 */
function createPillButton(id: string, icon: string, label: string, title: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = id;
  btn.className = 'pill-btn';
  const iconSpan = document.createElement('span');
  iconSpan.className = 'icon';
  iconSpan.textContent = icon;
  btn.appendChild(iconSpan);
  btn.appendChild(document.createTextNode(' ' + label));
  btn.title = title;
  btn.setAttribute('aria-label', title);
  return btn;
}

/**
 * Create a circular transport button
 */
function createTransportButton(id: string, icon: SVGSVGElement, title: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = id;
  btn.className = 'transport-btn';
  btn.appendChild(icon);
  btn.title = title;
  btn.setAttribute('aria-label', title);
  return btn;
}

/**
 * Wire up event handlers
 */
function wireEventHandlers(els: ReaderElements, core: ReaderCore): void {
  // ==========================================================================
  // Landing page buttons
  // ==========================================================================

  // Load text button (landing)
  els.loadTextBtn.addEventListener('click', () => {
    loadTextFromInput(els, core);
  });

  // Sample text button (landing)
  els.sampleTextBtn.addEventListener('click', async () => {
    await loadSampleText(els, core);
  });

  // PDF import button - triggers file input
  els.pdfImportBtn.addEventListener('click', () => {
    els.pdfFileInput.click();
  });

  // PDF file input change handler
  els.pdfFileInput.addEventListener('change', async () => {
    const file = els.pdfFileInput.files?.[0];
    if (!file) return;

    // Reset file input so same file can be selected again
    els.pdfFileInput.value = '';

    await handlePdfImport(file, els, core);
  });

  // Enter XR button (landing) - starts XR session or preview mode
  els.landingEnterXRBtn.addEventListener('click', async () => {
    const manager = getXRManager();
    if (manager.active) {
      // Exit current session/preview
      if (manager.inPreview) {
        manager.endPreview();
      } else {
        await manager.endSession();
      }
      els.landingEnterXRBtn.textContent = '◉ Enter XR';
      // Hide XR buttons when exiting XR
      const xrButtonsRow = els.mobileCameraToggle.parentElement;
      if (xrButtonsRow) xrButtonsRow.hidden = true;
      els.mobileCameraToggle.textContent = 'Start Camera';
      // Show keyboard hints again
      els.keyboardHints.hidden = false;
    } else {
      els.landingEnterXRBtn.disabled = true;
      els.landingEnterXRBtn.textContent = 'Starting...';

      // Need some text loaded for XR - load sample if empty
      if (!core.words.length) {
        await loadSampleText(els, core);
      }

      const container = els.landingEnterXRBtn.closest('.reader-container') as HTMLElement;
      const xrAvailable = await isXRAvailable();

      let success = false;
      if (xrAvailable) {
        // Full WebXR session
        success = await manager.startSession(core, container);
      } else {
        // Fall back to 3D preview mode
        console.log('[XR] WebXR not available, starting preview mode');
        success = await manager.startPreview(core, container);
      }

      if (success) {
        els.landingEnterXRBtn.textContent = '◉ Exit XR';
        els.landingEnterXRBtn.disabled = false;
        // Hide keyboard hints in XR mode (not useful on mobile)
        els.keyboardHints.hidden = true;
        // Show XR buttons in preview mode (mobile)
        if (manager.previewActive) {
          const xrButtonsRow = els.mobileCameraToggle.parentElement;
          if (xrButtonsRow) xrButtonsRow.hidden = false;
          els.mobileCameraToggle.textContent = manager.cameraActive ? 'Stop Camera' : 'Start Camera';
        }
      } else {
        els.landingEnterXRBtn.textContent = '◉ Enter XR';
        els.landingEnterXRBtn.disabled = false;
      }
    }
  });

  // Camera toggle button (mobile AR mode)
  // Shared handler for both click and touch
  const handleCameraToggle = async () => {
    const manager = getXRManager();
    if (!manager.previewActive) {
      return;
    }

    // Prevent double-triggering
    if (els.mobileCameraToggle.disabled) {
      return;
    }

    els.mobileCameraToggle.disabled = true;
    const cameraOn = await manager.toggleCameraMode();
    els.mobileCameraToggle.textContent = cameraOn ? 'Stop Camera' : 'Start Camera';
    els.mobileCameraToggle.disabled = false;
  };

  els.mobileCameraToggle.addEventListener('click', handleCameraToggle);

  // Touch handler for iOS - trigger on touchend to avoid click delay issues
  els.mobileCameraToggle.addEventListener('touchend', (e) => {
    e.preventDefault(); // Prevent synthetic click (we handle it here)
    e.stopPropagation();
    handleCameraToggle();
  });

  // Exit XR button (mobile)
  els.mobileExitXRBtn.addEventListener('click', async () => {
    const manager = getXRManager();
    if (manager.inPreview) {
      manager.endPreview();
    } else if (manager.active) {
      await manager.endSession();
    }
    // Reset landing Enter XR button
    els.landingEnterXRBtn.textContent = '◉ Enter XR';
    // Reset reader Enter XR Mode button
    els.enterXRBtn.textContent = '';
    els.enterXRBtn.appendChild(document.createTextNode('Enter'));
    els.enterXRBtn.appendChild(document.createElement('br'));
    els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
    // Hide XR buttons row and reset state
    const xrButtonsRow = els.mobileCameraToggle.parentElement;
    if (xrButtonsRow) xrButtonsRow.hidden = true;
    els.mobileCameraToggle.textContent = 'Start Camera';
    // Show keyboard hints again
    els.keyboardHints.hidden = false;
  });

  // ==========================================================================
  // Reader page buttons
  // ==========================================================================

  // Load text button (reader)
  els.readerLoadTextBtn.addEventListener('click', () => {
    // Return to landing to load new text
    core.clear();
  });

  // Sample text button (reader)
  els.readerSampleTextBtn.addEventListener('click', async () => {
    await loadSampleText(els, core);
  });

  // Enter XR button (reader) - same logic as landing button
  els.enterXRBtn.addEventListener('click', async () => {
    const manager = getXRManager();
    if (manager.active) {
      // Exit current session/preview
      if (manager.inPreview) {
        manager.endPreview();
      } else {
        await manager.endSession();
      }
      els.enterXRBtn.textContent = '';
      els.enterXRBtn.appendChild(document.createTextNode('Enter'));
      els.enterXRBtn.appendChild(document.createElement('br'));
      els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
      // Hide XR buttons when exiting XR
      const xrButtonsRow = els.mobileCameraToggle.parentElement;
      if (xrButtonsRow) xrButtonsRow.hidden = true;
      els.mobileCameraToggle.textContent = 'Start Camera';
      // Show keyboard hints again
      els.keyboardHints.hidden = false;
    } else {
      els.enterXRBtn.disabled = true;
      els.enterXRBtn.textContent = 'Starting...';

      const xrAvailable = await isXRAvailable();

      let success = false;
      if (xrAvailable) {
        success = await manager.startSession(core, els.container);
      } else {
        console.log('[XR] WebXR not available, starting preview mode');
        success = await manager.startPreview(core, els.container);
      }

      if (success) {
        els.enterXRBtn.textContent = '';
        els.enterXRBtn.appendChild(document.createTextNode('Exit'));
        els.enterXRBtn.appendChild(document.createElement('br'));
        els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
        els.enterXRBtn.disabled = false;
        // Hide keyboard hints in XR mode
        els.keyboardHints.hidden = true;
        // Show XR buttons in preview mode (mobile)
        if (manager.previewActive) {
          const xrButtonsRow = els.mobileCameraToggle.parentElement;
          if (xrButtonsRow) xrButtonsRow.hidden = false;
          els.mobileCameraToggle.textContent = manager.cameraActive ? 'Stop Camera' : 'Start Camera';
        }
      } else {
        els.enterXRBtn.textContent = '';
        els.enterXRBtn.appendChild(document.createTextNode('Enter'));
        els.enterXRBtn.appendChild(document.createElement('br'));
        els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
        els.enterXRBtn.disabled = false;
      }
    }
  });

  // Play/Pause button
  els.playPauseBtn.addEventListener('click', () => {
    if (core.status === 'finished') {
      // If finished, restart from beginning
      core.readAgain();
    }
    core.togglePlayPause();
  });

  // Skip to start button
  els.skipStartBtn.addEventListener('click', () => {
    core.restart();
  });

  // Rewind button
  els.rewindBtn.addEventListener('click', () => {
    core.rewind();
  });

  // Fast-forward button
  els.fastForwardBtn.addEventListener('click', () => {
    core.fastForward();
  });

  // Skip to end button
  els.skipEndBtn.addEventListener('click', () => {
    // Skip to last word
    if (core.wordCount > 0) {
      core.seekTo(core.wordCount - 1);
    }
  });

  // Progress slider (scrubbing)
  els.progressSlider.addEventListener('input', () => {
    const index = parseInt(els.progressSlider.value, 10);
    core.seekTo(index);
  });

  // WPM slider
  els.wpmSlider.addEventListener('input', () => {
    const wpm = parseInt(els.wpmSlider.value, 10);
    core.setWpm(wpm);
    updateWpmDisplay(wpm);
    // Sync mobile slider
    els.mobileWpmSlider.value = String(wpm);
  });

  // ==========================================================================
  // Mobile controls (mirror desktop controls)
  // ==========================================================================

  els.mobilePlayPauseBtn.addEventListener('click', () => {
    if (core.status === 'finished') {
      core.restart();
      core.play();
    } else if (core.status === 'playing') {
      core.pause();
    } else {
      core.play();
    }
  });

  els.mobileSkipStartBtn.addEventListener('click', () => {
    core.restart();
  });

  els.mobileRewindBtn.addEventListener('click', () => {
    core.rewind();
  });

  els.mobileFastForwardBtn.addEventListener('click', () => {
    core.fastForward();
  });

  els.mobileSkipEndBtn.addEventListener('click', () => {
    if (core.wordCount > 0) {
      core.seekTo(core.wordCount - 1);
    }
  });

  els.mobileWpmSlider.addEventListener('input', () => {
    const wpm = parseInt(els.mobileWpmSlider.value, 10);
    core.setWpm(wpm);
    updateWpmDisplay(wpm);
    // Sync desktop slider
    els.wpmSlider.value = String(wpm);
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', handleKeyDown);

  // ==========================================================================
  // Finished modal actions (FR-010, FR-011, FR-012)
  // ==========================================================================

  // X button: dismiss without resetting progress (FR-010)
  els.modalCloseBtn.addEventListener('click', () => {
    hideFinishedModal();
  });

  // Restart button: return to landing state (FR-011)
  els.modalRestartBtn.addEventListener('click', async () => {
    hideFinishedModal();
    // Exit XR mode if active
    const manager = getXRManager();
    if (manager.active) {
      if (manager.inPreview) {
        manager.endPreview();
      } else {
        await manager.endSession();
      }
      // Reset XR buttons
      els.landingEnterXRBtn.textContent = '◉ Enter XR';
      els.enterXRBtn.textContent = '';
      els.enterXRBtn.appendChild(document.createTextNode('Enter'));
      els.enterXRBtn.appendChild(document.createElement('br'));
      els.enterXRBtn.appendChild(document.createTextNode('XR Mode'));
      const xrButtonsRow = els.mobileCameraToggle.parentElement;
      if (xrButtonsRow) xrButtonsRow.hidden = true;
      els.keyboardHints.hidden = false;
    }
    core.clear();
  });

  // Read again button: reset to first word, stay on reader (FR-012)
  els.modalReadAgainBtn.addEventListener('click', () => {
    hideFinishedModal();
    core.readAgain();
  });

  // Close modal on backdrop click
  els.finishedModal.addEventListener('click', (e) => {
    if (e.target === els.finishedModal.querySelector('.modal-backdrop')) {
      hideFinishedModal();
    }
  });
}

/**
 * Load text from the textarea input
 */
function loadTextFromInput(els: ReaderElements, core: ReaderCore, docTitle?: string): void {
  const text = els.textInput.value.trim();
  if (text) {
    const success = core.setText(text);
    if (success) {
      els.textInput.value = '';
      // Show document title if provided, otherwise hide it
      if (docTitle) {
        els.docTitle.textContent = docTitle;
        els.docTitle.hidden = false;
      } else {
        els.docTitle.textContent = '';
        els.docTitle.hidden = true;
      }
    }
  }
}

/**
 * Load sample text
 */
async function loadSampleText(els: ReaderElements, core: ReaderCore): Promise<void> {
  els.sampleTextBtn.disabled = true;
  els.readerSampleTextBtn.disabled = true;
  try {
    const text = await getSampleText();
    const success = core.setText(text);
    if (success) {
      els.textInput.value = '';
      // Sample text has a known title
      els.docTitle.textContent = 'Elementary Principles of Statistical Mechanics';
      els.docTitle.hidden = false;
    }
  } catch (error) {
    console.error('Failed to load sample text:', error);
  } finally {
    els.sampleTextBtn.disabled = false;
    els.readerSampleTextBtn.disabled = false;
  }
}

/**
 * Handle PDF file import
 */
async function handlePdfImport(file: File, els: ReaderElements, core: ReaderCore): Promise<void> {
  if (!state) return;

  // Show loading state
  els.pdfImportBtn.disabled = true;
  els.pdfImportBtn.textContent = '';
  const iconSpan = document.createElement('span');
  iconSpan.className = 'icon';
  iconSpan.textContent = '📑';
  els.pdfImportBtn.appendChild(iconSpan);
  els.pdfImportBtn.appendChild(document.createTextNode(' Extracting...'));
  els.pdfStatus.hidden = true;

  try {
    // Extract text from PDF
    const extraction = await extractPdfText(file);

    // Clean the text (always clean, no toggle)
    const cleanupResult = cleanPdfText(extraction);

    // Check if scanned PDF
    if (cleanupResult.isLikelyScanned) {
      els.pdfStatus.textContent = 'OCR not supported in MVP. This appears to be a scanned or image-only PDF.';
      els.pdfStatus.className = 'pdf-status warning';
      els.pdfStatus.hidden = false;
      return;
    }

    // Store result
    state.lastPdfResult = cleanupResult;

    // Always use cleaned text
    const text = cleanupResult.cleanedText;

    // Update UI
    els.textInput.value = text;

    // Automatically load the text into the reader
    loadTextFromInput(els, core, file.name.replace(/\.pdf$/i, ''));
  } catch (error) {
    console.error('PDF extraction failed:', error);
    els.pdfStatus.textContent = 'Failed to extract text from PDF. Please try a different file.';
    els.pdfStatus.className = 'pdf-status error';
    els.pdfStatus.hidden = false;
  } finally {
    els.pdfImportBtn.disabled = false;
    els.pdfImportBtn.textContent = '';
    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.textContent = '📑';
    els.pdfImportBtn.appendChild(icon);
    els.pdfImportBtn.appendChild(document.createTextNode(' Import PDF'));
  }
}

/**
 * Handle keyboard shortcuts
 */
function handleKeyDown(event: KeyboardEvent): void {
  if (!state || !elements) return;

  // Ignore if typing in textarea
  if (event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement) {
    return;
  }

  const core = state.core;

  switch (event.code) {
    case 'Space':
      event.preventDefault();
      if (core.status === 'finished') {
        core.readAgain();
      }
      core.togglePlayPause();
      break;
    case 'ArrowLeft':
      event.preventDefault();
      core.rewind();
      break;
    case 'ArrowRight':
      event.preventDefault();
      core.fastForward();
      break;
    case 'ArrowUp':
      event.preventDefault();
      core.adjustWpm(PACING_CONFIG.WPM_STEP);
      if (elements) {
        elements.wpmSlider.value = String(core.wpm);
        updateWpmDisplay(core.wpm);
      }
      break;
    case 'ArrowDown':
      event.preventDefault();
      core.adjustWpm(-PACING_CONFIG.WPM_STEP);
      if (elements) {
        elements.wpmSlider.value = String(core.wpm);
        updateWpmDisplay(core.wpm);
      }
      break;
    case 'KeyR':
      if (!event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        core.restart();
      }
      break;
    case 'Escape':
      // Exit XR/preview mode
      event.preventDefault();
      const manager = getXRManager();
      if (manager.active) {
        if (manager.inPreview) {
          manager.endPreview();
        } else {
          manager.endSession();
        }
        // Reset button text
        if (elements) {
          elements.landingEnterXRBtn.textContent = '◉ Enter XR';
        }
      }
      break;
  }
}

/**
 * Update UI based on status
 */
function updateUIForStatus(status: ReaderStatus): void {
  if (!elements) return;

  const isIdle = status === 'idle';
  const isPlaying = status === 'playing';
  const isFinished = status === 'finished';

  // Toggle landing vs reader content
  elements.landingContent.hidden = !isIdle;
  elements.readerContent.hidden = isIdle;

  // Update play/pause button icon (desktop + mobile)
  if (isFinished) {
    setButtonIcon(elements.playPauseBtn, createIcon('restart', 24));
    setButtonIcon(elements.mobilePlayPauseBtn, createIcon('restart', 24));
    elements.playPauseBtn.title = 'Read again';
    elements.mobilePlayPauseBtn.title = 'Read again';
    elements.playPauseBtn.setAttribute('aria-label', 'Read again');
    elements.mobilePlayPauseBtn.setAttribute('aria-label', 'Read again');
  } else {
    setButtonIcon(elements.playPauseBtn, createIcon(isPlaying ? 'pause' : 'play', 24));
    setButtonIcon(elements.mobilePlayPauseBtn, createIcon(isPlaying ? 'pause' : 'play', 24));
    elements.playPauseBtn.title = isPlaying ? 'Pause' : 'Play';
    elements.mobilePlayPauseBtn.title = isPlaying ? 'Pause' : 'Play';
    elements.playPauseBtn.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    elements.mobilePlayPauseBtn.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
  }

  // Update button states (desktop)
  elements.playPauseBtn.disabled = isIdle;
  elements.skipStartBtn.disabled = isIdle;
  elements.rewindBtn.disabled = isIdle;
  elements.fastForwardBtn.disabled = isIdle || isFinished;
  elements.skipEndBtn.disabled = isIdle || isFinished;

  // Update button states (mobile)
  elements.mobilePlayPauseBtn.disabled = isIdle;
  elements.mobileSkipStartBtn.disabled = isIdle;
  elements.mobileRewindBtn.disabled = isIdle;
  elements.mobileFastForwardBtn.disabled = isIdle || isFinished;
  elements.mobileSkipEndBtn.disabled = isIdle || isFinished;

  // Toggle playing class for visual feedback (desktop + mobile)
  elements.playPauseBtn.classList.toggle('playing', isPlaying);
  elements.mobilePlayPauseBtn.classList.toggle('playing', isPlaying);

  // Show/hide mobile controls based on state
  elements.mobileControls.hidden = isIdle;

  // Show/hide finished state in panel
  elements.wordDisplay.hidden = isFinished;
  elements.finishedIcon.hidden = !isFinished;
  elements.finishedText.hidden = !isFinished;
  elements.rsvpPanel.classList.toggle('finished', isFinished);

  // Start/stop animation loop
  if (isPlaying) {
    startAnimationLoop();
  } else {
    stopAnimationLoop();
  }
}

/**
 * Update word display with auto-scaling for long words
 */
function updateWordDisplay(word: string): void {
  if (!elements) return;

  const wordEl = elements.wordDisplay;
  const panelEl = elements.rsvpPanel;

  // Reset any previous scaling
  wordEl.style.fontSize = '';

  // Set the word
  wordEl.textContent = word;

  // Check if word overflows and scale down if needed
  requestAnimationFrame(() => {
    const panelWidth = panelEl.clientWidth;
    const padding = 48; // Account for panel padding (--space-5 * 2 roughly)
    const maxWidth = panelWidth - padding;

    if (wordEl.scrollWidth > maxWidth && maxWidth > 0) {
      // Calculate scale factor to fit
      const scale = maxWidth / wordEl.scrollWidth;
      const currentSize = parseFloat(getComputedStyle(wordEl).fontSize);
      const newSize = Math.max(12, currentSize * scale * 0.95); // 0.95 for safety margin, min 12px
      wordEl.style.fontSize = `${newSize}px`;
    }
  });
}

/**
 * Update progress slider and text
 */
function updateProgress(index: number, total: number): void {
  if (!elements) return;

  // Update text: "X/Y words" (1-based for display)
  const displayIndex = Math.min(index + 1, total);
  elements.progressText.textContent = `${displayIndex}/${total} words`;

  // Update slider (0-based internally)
  elements.progressSlider.max = String(Math.max(0, total - 1));
  elements.progressSlider.value = String(index);
}

/**
 * Update WPM display
 */
function updateWpmDisplay(wpm: number): void {
  if (!elements) return;
  elements.wpmValue.textContent = String(wpm);
  elements.mobileWpmValue.textContent = String(wpm);
}

/**
 * Handle finished state - show modal (FR-009)
 */
function handleFinished(): void {
  if (!elements) return;
  // The finished state is shown in the panel via updateUIForStatus
  // Show the finished modal
  showFinishedModal();
}

/**
 * Show the finished modal
 */
function showFinishedModal(): void {
  if (!elements) return;
  elements.finishedModal.hidden = false;
  // Focus the "Read again" button for keyboard users
  elements.modalReadAgainBtn.focus();
}

/**
 * Hide the finished modal
 */
function hideFinishedModal(): void {
  if (!elements) return;
  elements.finishedModal.hidden = true;
  // Return focus to play button
  elements.playPauseBtn.focus();
}

/**
 * Read CSS token value
 */
function getCSSToken(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Convert CSS color to hex for QR library
 */
function cssColorToHex(color: string): string {
  // If already hex, return as-is
  if (color.startsWith('#')) return color;

  // Parse rgb/rgba
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  return color;
}

/**
 * Generate QR code for landing page (dark on white background)
 */
async function generateLandingQRCode(canvas: HTMLCanvasElement, url: string): Promise<void> {
  try {
    // Landing QR uses dark foreground on white background (tokens)
    const foreground = cssColorToHex(getCSSToken('--color-black', '#0f1114'));
    const background = cssColorToHex(getCSSToken('--color-white', '#ffffff'));

    await QRCode.toCanvas(canvas, url, {
      width: 120,
      margin: 1,
      color: {
        dark: foreground,
        light: background,
      },
    });
  } catch (err) {
    console.error('Failed to generate landing QR code:', err);
  }
}

/**
 * Start the animation loop for tick updates
 */
function startAnimationLoop(): void {
  if (!state) return;
  if (state.animationFrameId !== null) return;

  state.lastTickTime = performance.now();

  const loop = (now: number) => {
    if (!state) return;

    const dt = now - state.lastTickTime;
    state.lastTickTime = now;

    state.core.tick(dt);

    if (state.core.status === 'playing') {
      state.animationFrameId = requestAnimationFrame(loop);
    } else {
      state.animationFrameId = null;
    }
  };

  state.animationFrameId = requestAnimationFrame(loop);
}

/**
 * Stop the animation loop
 */
function stopAnimationLoop(): void {
  if (!state) return;
  if (state.animationFrameId !== null) {
    cancelAnimationFrame(state.animationFrameId);
    state.animationFrameId = null;
  }
}

/**
 * Cleanup reader UI
 */
export async function cleanupReaderUI(): Promise<void> {
  stopAnimationLoop();
  document.removeEventListener('keydown', handleKeyDown);

  // Cleanup XR session
  if (state?.cleanupXR) {
    await state.cleanupXR();
  }

  // Cleanup pairing
  if (state?.cleanupPairing) {
    state.cleanupPairing();
  }
  cleanupPairingUI();

  state = null;
  elements = null;
}
